import { zonedTimeToUtc } from 'date-fns-tz'

import { type LateCheckInReasonCode, REASON_NOTE_MAX_LENGTH } from '../domain/LateCheckInReason'

/**
 * True when `date` is a real calendar day and `time` is `HH:mm` in 00:00–23:59.
 * Incomplete or overflowing values (for example `29:00` or 31 Feb) are false.
 */
export function isValidDeclaredDateTime(
  date: string | null | undefined,
  time: string | null | undefined
): boolean {
  if (!date || !time) {
    return false
  }

  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time)

  if (!dateMatch || !timeMatch) {
    return false
  }

  const year = Number(dateMatch[1])
  const month = Number(dateMatch[2])
  const day = Number(dateMatch[3])
  const hours = Number(timeMatch[1])
  const minutes = Number(timeMatch[2])

  if (month < 1 || month > 12 || hours > 23 || minutes > 59) {
    return false
  }

  const calendarDay = new Date(Date.UTC(year, month - 1, day))

  return (
    calendarDay.getUTCFullYear() === year &&
    calendarDay.getUTCMonth() === month - 1 &&
    calendarDay.getUTCDate() === day
  )
}

/**
 * Resolves the declared date (`YYYY-MM-DD…`) and `HH:mm` time in the facility
 * timezone; `null` while either is incomplete.
 */
export function getDeclaredCheckInTime(
  date: string | null | undefined,
  time: string | null | undefined,
  timezone: string
): Date | null {
  if (!date || !time) {
    return null
  }

  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time)

  if (!dateMatch || !timeMatch) {
    return null
  }

  const [, year, month, day] = dateMatch
  const [, hours, minutes] = timeMatch

  return zonedTimeToUtc(`${year}-${month}-${day}T${hours}:${minutes}:00`, timezone)
}

export function isLateCheckIn({
  stopType,
  date,
  time,
  appointmentWindowEnd,
  timezone,
}: {
  stopType: string
  date: string | null | undefined
  time: string | null | undefined
  appointmentWindowEnd?: string | null
  timezone?: string | null
}): boolean {
  if (stopType !== 'in' || !appointmentWindowEnd || !timezone) {
    return false
  }

  const declaredTime = getDeclaredCheckInTime(date, time, timezone)

  return declaredTime !== null && declaredTime.getTime() > Date.parse(appointmentWindowEnd)
}

export function isLateReasonValid({
  isLate,
  reasonCode,
  reasonNote,
  reasonRequired = true,
}: {
  isLate: boolean
  reasonCode: LateCheckInReasonCode | ''
  reasonNote: string
  reasonRequired?: boolean
}): boolean {
  if (!isLate) {
    return true
  }

  if (!reasonCode) {
    return !reasonRequired
  }

  if (reasonCode === 'OTHER') {
    const trimmedNote = reasonNote.trim()
    return trimmedNote.length > 0 && trimmedNote.length <= REASON_NOTE_MAX_LENGTH
  }

  return reasonNote.trim().length <= REASON_NOTE_MAX_LENGTH
}
